import { useMemo, useState } from "react";
import { Button } from "@headlessui/react";
import { PlusIcon } from "@heroicons/react/24/solid";
import { UserRole } from "../../../app/userSlice";
import ModalFrame from "../../../components/ModalFrame";
import Tabs, { SubTab } from "../../../components/Tabs";
import { SimpleOption } from "../../../components/input/SimpleListbox";
import AddExistingCoursesToUserForm from "../../courses/AddExistingCoursesToUserForm";
import AddCourseByAdminForm from "../../courses/AddCourseByAdminForm";
import { UserCourseViewModel, UserDistrictViewModel, UserSchoolViewModel } from "../usersApiSlice";

export enum AddCoursesSubTabs {
	Existing = "Existing Courses",
	New = "New Course",
}

interface AddCoursesProps {
	userId: number;
	userRole: UserRole;
	districts: UserDistrictViewModel[];
	schools: UserSchoolViewModel[];
	courses: UserCourseViewModel[];
	setErrorMessage: (message: string) => void;
}

export default function AddCoursesToUserButton(props: AddCoursesProps) {
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [currentTabName, setCurrentTabName] = useState(AddCoursesSubTabs.Existing);

	const activeSchools = useMemo(
		() => props.schools.filter((s) => s.active && s.canEditSchool),
		[props.schools]
	);

	const districtOptions = useMemo(() => {
		const districtIdsWithActiveSchools = new Set(activeSchools.map((s) => s.districtID));

		// Only include districts that have at least one active school
		const options = props.districts
			.filter((d) => districtIdsWithActiveSchools.has(d.districtID))
			.map((d) => ({ value: d.districtID, label: d.name }) as SimpleOption);

		// If registered in an active school without a district
		if (activeSchools.some((s) => s.districtID == 0)) {
			options.unshift({ value: 0, label: "None" } as SimpleOption);
		}

		return options;
	}, [props.districts, activeSchools]);

	const initialSubTabs: SubTab[] = useMemo(
		() => [
			{
				name: AddCoursesSubTabs.Existing,
				current: currentTabName === AddCoursesSubTabs.Existing,
				content: (
					<AddExistingCoursesToUserForm
						districtOptions={districtOptions}
						activeSchools={activeSchools}
						allSchoolCount={props.schools.length}
						courses={props.courses}
						userRole={props.userRole}
						userId={props.userId}
						onClose={handleCloseForm}
						setErrorMessage={props.setErrorMessage}
					/>
				),
			},
			{
				name: AddCoursesSubTabs.New,
				current: currentTabName === AddCoursesSubTabs.New,
				content: (
					<AddCourseByAdminForm
						userId={props.userId}
						activeSchools={activeSchools}
						allSchoolCount={props.schools.length}
						districtOptions={districtOptions}
						onClose={handleCloseForm}
						setErrorMessage={props.setErrorMessage}
					/>
				),
			},
		],
		[props, currentTabName, activeSchools, districtOptions]
	);

	function handleOpenForm() {
		setIsFormOpen(true);
		setCurrentTabName(AddCoursesSubTabs.Existing);
	}

	function handleCloseForm() {
		setIsFormOpen(false);
	}

	return (
		<>
			<Button id="add-district-button" onClick={handleOpenForm} className="btn btn-primary">
				<span className="flex items-center">
					<PlusIcon className="size-4 mr-2" /> Add
				</span>
			</Button>

			<ModalFrame
				open={isFormOpen}
				title="Add User To Courses"
				onClose={handleCloseForm}
				maxWidth="max-w-[850px]"
				overflow="overflow-visible">
				<Tabs
					initialSubTabs={initialSubTabs}
					ariaLabel="Add User To District Tabs"
					containerClass="mt-1.5"
					tabPadding="p-2"
				/>
			</ModalFrame>
		</>
	);
}
